import {
  BISHOP,
  BLACK,
  type Color,
  FLAG_CAPTURE,
  FLAG_CASTLE,
  FLAG_DROP,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  TYPE_MASK,
  WHITE,
  colorBit,
  encodeMove,
  moveFlags,
  moveFrom,
  moveTo,
  other,
} from '../board.ts';
import { DIAGONAL, KING_STEPS, KNIGHT_JUMPS, ORTHOGONAL, type Vec } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 10 (draft). The edges wrap around: the a- and h-files are next to each other, so the
// board is a cylinder. Anything that runs off one side comes back on the other (rooks,
// bishops, queens, knights, kings and pawn captures); ranks don't wrap. The normal moves come
// from the engine's tables; `generate` adds the ones that cross the seam and `attacks` mirrors
// them, so checks across the seam count.

const LEAPERS: [number, readonly Vec[]][] = [
  [KNIGHT, KNIGHT_JUMPS],
  [KING, KING_STEPS],
];
const SLIDERS: [number, readonly Vec[]][] = [
  [ROOK, ORTHOGONAL],
  [BISHOP, DIAGONAL],
  [QUEEN, [...ORTHOGONAL, ...DIAGONAL]],
];
const PAWN_CAPTURES: readonly Vec[] = [[-1, 1], [1, 1]];
const PROMOTIONS = [QUEEN, ROOK, BISHOP, KNIGHT];

// Scratch marks for deduplicating a piece's targets (squares stamped with the current pass).
const seen = new Int32Array(64);
let pass = 0;

/** Square reached by a step that may cross the seam, or -1 off the top/bottom. */
function step(sq: number, df: number, dr: number): number {
  const r = (sq >> 3) + dr;
  if (r < 0 || r > 7) return -1;
  return r * 8 + (((sq & 7) + df + 8) & 7);
}

const crosses = (sq: number, df: number) => (sq & 7) + df < 0 || (sq & 7) + df > 7;

/** Marks everything a piece reaches without crossing the seam (already generated normally). */
function markDirect(board: Int8Array, sq: number, type: number, color: Color) {
  if (type === PAWN) return;
  for (const [t, vecs] of LEAPERS) {
    if (t !== type) continue;
    for (const [df, dr0] of vecs) {
      const dr = color === WHITE ? dr0 : -dr0;
      if (crosses(sq, df)) continue;
      const to = step(sq, df, dr);
      if (to >= 0) seen[to] = pass;
    }
  }
  for (const [t, dirs] of SLIDERS) {
    if (t !== type) continue;
    for (const [df, dr] of dirs) {
      let cur = sq;
      while (!crosses(cur, df)) {
        cur = step(cur, df, dr);
        if (cur < 0) break;
        seen[cur] = pass;
        if (board[cur] !== 0) break;
      }
    }
  }
  seen[sq] = pass;
}

function push(out: number[], board: Int8Array, from: number, to: number, themBit: number, capturesOnly: boolean) {
  if (seen[to] === pass) return;
  seen[to] = pass;
  if (board[to] & themBit) out.push(encodeMove(from, to, 0, FLAG_CAPTURE));
  else if (board[to] === 0 && !capturesOnly) out.push(encodeMove(from, to));
}

/** Moves across the seam for the side to move. */
function seamMoves(pos: Position, out: number[], capturesOnly: boolean) {
  const b = pos.board;
  const us = pos.turn;
  const usBit = colorBit(us);
  const themBit = colorBit(other(us));
  for (let sq = 0; sq < 64; sq++) {
    const p = b[sq];
    if (!(p & usBit)) continue;
    const type = p & TYPE_MASK;
    pass++;
    if (type === PAWN) {
      for (const [df, dr0] of PAWN_CAPTURES) {
        if (!crosses(sq, df)) continue;
        const to = step(sq, df, us === WHITE ? dr0 : -dr0);
        if (to < 0 || !(b[to] & themBit)) continue;
        const last = to >> 3 === (us === WHITE ? 7 : 0);
        if (last) for (const promo of PROMOTIONS) out.push(encodeMove(sq, to, promo, FLAG_CAPTURE));
        else out.push(encodeMove(sq, to, 0, FLAG_CAPTURE));
      }
      continue;
    }
    markDirect(b, sq, type, us);
    for (const [t, vecs] of LEAPERS) {
      if (t !== type) continue;
      for (const [df, dr] of vecs) {
        if (!crosses(sq, df)) continue;
        const to = step(sq, df, dr);
        if (to >= 0 && !(b[to] & usBit)) push(out, b, sq, to, themBit, capturesOnly);
      }
    }
    for (const [t, dirs] of SLIDERS) {
      if (t !== type) continue;
      for (const [df, dr] of dirs) {
        let cur = sq;
        let crossed = false;
        for (;;) {
          if (crosses(cur, df)) crossed = true;
          cur = step(cur, df, dr);
          if (cur < 0 || cur === sq) break;
          const q = b[cur];
          if (crossed && !(q & usBit)) push(out, b, sq, cur, themBit, capturesOnly);
          if (q !== 0) break;
        }
      }
    }
  }
}

/** Whether a piece of `by` attacks `sq` across the seam. */
export function seamAttack(board: Int8Array, sq: number, by: Color): boolean {
  const bit = colorBit(by);
  for (const [t, vecs] of LEAPERS) {
    for (const [df, dr] of vecs) {
      // Knight and king moves are symmetric, so walking the vector backwards finds the source.
      if (!crosses(sq, -df)) continue;
      const from = step(sq, -df, -dr);
      if (from >= 0 && board[from] === (t | bit)) return true;
    }
  }
  for (const [df, dr0] of PAWN_CAPTURES) {
    const dr = by === WHITE ? dr0 : -dr0;
    if (!crosses(sq, -df)) continue;
    const from = step(sq, -df, -dr);
    if (from >= 0 && board[from] === (PAWN | bit)) return true;
  }
  for (const [df, dr] of [...ORTHOGONAL, ...DIAGONAL]) {
    const diagonal = df !== 0 && dr !== 0;
    let cur = sq;
    let crossed = false;
    for (;;) {
      if (crosses(cur, -df)) crossed = true;
      cur = step(cur, -df, -dr);
      if (cur < 0 || cur === sq) break;
      const q = board[cur];
      if (q === 0) continue;
      if (crossed && q & bit) {
        const t = q & TYPE_MASK;
        if (t === QUEEN || t === (diagonal ? BISHOP : ROOK)) return true;
      }
      break;
    }
  }
  return false;
}

export const wrap: Variant = {
  id: 'wrap',
  info: {
    name: 'Wraparound Chess',
    but: 'the edges wrap around',
    tagline: 'The a-file and h-file are neighbours. Leave on the left, come back on the right.',
    summary: 'The board is a **cylinder**: anything that runs off the left edge comes back on the right, and the other way round.',
    rules: [
      'The a- and h-files are joined: moves that run off one side come back on the other.',
      'Rooks, bishops, queens, knights, kings and pawn captures all wrap. The top and bottom don’t.',
      'Checks across the edge count, so watch both sides of your king.',
      'Castling and en passant work as usual (en passant doesn’t wrap).',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#2ed3b7',
    emoji: '🔁',
    bots: ['Loop', 'Side Door', 'Round Trip', 'Globe Trotter', 'Infinite Loop'],
  },
  startFen: STANDARD_FEN,
  // Wrapping keeps bishops on their colour and gives a lone piece no new mates.
  insufficientMaterial: true,

  generate(pos, out, capturesOnly) {
    seamMoves(pos, out, capturesOnly);
  },

  attacks: seamAttack,

  afterMove(ctx: MoveContext) {
    // Only the UI needs to know (for its effect and sound).
    if (ctx.events && crossedSeam(ctx.prev.board, ctx.move)) ctx.fx('wrap', moveTo(ctx.move));
  },
};

/** Whether a move crossed the seam (for the UI): it isn't one the piece makes on a flat board. */
export function crossedSeam(board: Int8Array, move: number): boolean {
  if (moveFlags(move) & (FLAG_CASTLE | FLAG_DROP)) return false;
  const from = moveFrom(move);
  const to = moveTo(move);
  const p = board[from];
  if (!p) return false;
  const type = p & TYPE_MASK;
  if (type === PAWN) return Math.abs((from & 7) - (to & 7)) > 1;
  pass++;
  markDirect(board, from, type, p & colorBit(WHITE) ? WHITE : BLACK);
  return seen[to] !== pass;
}
